import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { chatApi } from '../services/api.js';

function generateId() {
  return Date.now().toString(36) + Math.random().toString(36).substring(2);
}

function getTitleFromMessage(message, maxLength = 30) {
  const text = message.trim();
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength).trim() + '...';
}

const streamEnabled = false;

export const useChatStore = defineStore('chat', () => {
  // State
  const conversations = ref([]);
  const currentConversationId = ref(null);
  const isLoading = ref(false);
  const isSending = ref(false);
  const error = ref(null);
  const sidebarOpen = ref(true);
  let currentAbortController = null;

  // Computed
  const currentConversation = computed(() => {
    return conversations.value.find((c) => c.id === currentConversationId.value) || null;
  });

  const currentMessages = computed(() => {
    return currentConversation.value?.messages || [];
  });

  const sortedConversations = computed(() => {
    return [...conversations.value].sort((a, b) => b.updatedAt - a.updatedAt);
  });

  const hasConversations = computed(() => {
    return conversations.value.length > 0;
  });

  // Actions

  async function loadSessions() {
    isLoading.value = true;
    error.value = null;

    try {
      const messages = await chatApi.getSessions();
      const grouped = {};
      messages.forEach((msg) => {
        if (!grouped[msg.session_id]) grouped[msg.session_id] = [];
        grouped[msg.session_id].push(msg);
      });

      conversations.value = Object.entries(grouped).map(([sessionId, msgs]) => {
        const firstUserMsg = msgs.find((m) => m.role === 'user');
        const lastMsg = msgs[msgs.length - 1];
        return {
          id: sessionId,
          sessionId,
          title: getTitleFromMessage(firstUserMsg?.content || 'New Chat'),
          messages: msgs.map((m) => ({
            id: generateId(),
            role: m.role,
            content: m.content,
            retrievedCount: m.metadata?.citations?.length,
            model: m.metadata?.model || null,
            promptTokens: m.metadata?.prompt_tokens || 0,
            completionTokens: m.metadata?.completion_tokens || 0,
            costUsd: m.metadata?.cost_usd || null,
            createdAt: new Date(m.created_at).getTime(),
          })),
          updatedAt: new Date(lastMsg.created_at).getTime(),
        };
      });
    } catch (err) {
      console.error('Failed to load sessions:', err);
      error.value = err.response?.data?.message || err.message || 'Failed to load sessions';
      conversations.value = [];
    } finally {
      isLoading.value = false;
    }
  }

  function createConversation() {
    conversations.value.unshift({
      id: generateId(),
      sessionId: null,
      title: 'New Chat',
      messages: [],
      updatedAt: Date.now(),
    });
    currentConversationId.value = conversations.value[0].id;
    error.value = null;
    // Return the reactive proxy from the array, NOT the plain object
    return conversations.value[0];
  }

  function selectConversation(conversationId) {
    currentConversationId.value = conversationId;
    error.value = null;
  }

  async function deleteConversation(conversationId) {
    const conversation = conversations.value.find((c) => c.id === conversationId);
    if (!conversation) return;

    if (conversation.sessionId) {
      try {
        await chatApi.deleteSession(conversation.sessionId);
      } catch (err) {
        console.error('Failed to delete session:', err);
        error.value = err.response?.data?.message || err.message || 'Failed to delete session';
        return;
      }
    }

    const index = conversations.value.findIndex((c) => c.id === conversationId);
    if (index !== -1) {
      conversations.value.splice(index, 1);
      if (currentConversationId.value === conversationId) {
        currentConversationId.value = conversations.value[0]?.id || null;
      }
    }
  }

  async function sendMessage(content) {
    if (!content.trim() || isSending.value) return;

    // Create conversation if none exists
    let conversation = currentConversation.value;
    if (!conversation) {
      conversation = createConversation();
    }

    // Add user message immediately
    const userMessage = {
      id: generateId(),
      role: 'user',
      content: content.trim(),
      createdAt: Date.now(),
    };
    conversation.messages.push(userMessage);

    // Update title if this is the first message
    if (conversation.messages.length === 1) {
      conversation.title = getTitleFromMessage(content);
    }

    conversation.updatedAt = Date.now();

    // Send to API
    isSending.value = true;
    error.value = null;
    currentAbortController = new AbortController();

    if (streamEnabled) {
      return await sendStreamed(conversation, content.trim(), currentAbortController.signal);
    } else {
      return await sendBlocking(conversation, content.trim(), currentAbortController.signal);
    }
  }

  async function sendStreamed(conversation, content, signal) {
    // Add placeholder and get the reactive proxy reference back from the array
    conversation.messages.push({
      id: generateId(),
      role: 'assistant',
      content: '',
      retrievedCount: null,
      model: null,
      promptTokens: 0,
      completionTokens: 0,
      costUsd: null,
      createdAt: Date.now(),
    });
    const msg = conversation.messages[conversation.messages.length - 1];

    try {
      const response = await chatApi.sendMessageStreamed(
        content,
        conversation.sessionId,
        (delta) => {
          msg.content += delta;
        },
        signal
      );

      if (response && !conversation.sessionId) {
        conversation.sessionId = response.session_id;
        conversation.id = response.session_id;
        currentConversationId.value = response.session_id;
      }

      if (response) {
        msg.retrievedCount = response.retrieved_count;
        msg.model = response.metadata?.model || null;
        msg.promptTokens = response.metadata?.prompt_tokens || 0;
        msg.completionTokens = response.metadata?.completion_tokens || 0;
        msg.costUsd = response.metadata?.cost_usd || null;
      }

      conversation.updatedAt = Date.now();
      return msg;
    } catch (err) {
      if (err.name === 'AbortError' || err.code === 'ERR_CANCELED') {
        if (!msg.content) {
          const idx = conversation.messages.indexOf(msg);
          if (idx !== -1) conversation.messages.splice(idx, 1);
        }
        return null;
      }

      error.value = err.response?.data?.message || err.message || 'Failed to send message';
      msg.content = `Sorry, I encountered an error: ${error.value}. Please try again.`;
      msg.isError = true;
      conversation.updatedAt = Date.now();
      throw err;
    } finally {
      isSending.value = false;
      currentAbortController = null;
    }
  }

  async function sendBlocking(conversation, content, signal) {
    try {
      const response = await chatApi.sendMessage(content, conversation.sessionId, signal);

      if (!conversation.sessionId) {
        conversation.sessionId = response.session_id;
        conversation.id = response.session_id;
        currentConversationId.value = response.session_id;
      }

      const assistantMessage = {
        id: generateId(),
        role: 'assistant',
        content: response.response,
        retrievedCount: response.retrieved_count,
        model: response.metadata?.model || null,
        promptTokens: response.metadata?.prompt_tokens || 0,
        completionTokens: response.metadata?.completion_tokens || 0,
        costUsd: response.metadata?.cost_usd || null,
        createdAt: Date.now(),
      };
      conversation.messages.push(assistantMessage);
      conversation.updatedAt = Date.now();
      return assistantMessage;
    } catch (err) {
      if (err.name === 'AbortError' || err.code === 'ERR_CANCELED') {
        return null;
      }

      error.value = err.response?.data?.message || err.message || 'Failed to send message';
      const errorMessage = {
        id: generateId(),
        role: 'assistant',
        content: `Sorry, I encountered an error: ${error.value}. Please try again.`,
        isError: true,
        createdAt: Date.now(),
      };
      conversation.messages.push(errorMessage);
      conversation.updatedAt = Date.now();
      throw err;
    } finally {
      isSending.value = false;
      currentAbortController = null;
    }
  }

  function toggleSidebar() {
    sidebarOpen.value = !sidebarOpen.value;
  }

  function clearError() {
    error.value = null;
  }

  function stopGenerating() {
    if (currentAbortController) {
      currentAbortController.abort();
      currentAbortController = null;
      isSending.value = false;
    }
  }

  // Initialize — load sessions from backend
  loadSessions();

  return {
    // State
    conversations,
    currentConversationId,
    isLoading,
    isSending,
    error,
    sidebarOpen,

    // Computed
    currentConversation,
    currentMessages,
    sortedConversations,
    hasConversations,

    // Actions
    loadSessions,
    createConversation,
    selectConversation,
    deleteConversation,
    sendMessage,
    stopGenerating,
    toggleSidebar,
    clearError,
  };
});
